import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { ScheduleGridContextValue, ScheduleItemSize, SizeThresholds } from './types';
import { getMinutesDiff, getMinutesSinceMidnight } from './utils';

const ScheduleGridContext = createContext<ScheduleGridContextValue | null>(null);

export function useScheduleGridContext(): ScheduleGridContextValue {
  const context = useContext(ScheduleGridContext);
  if (!context) {
    throw new Error('useScheduleGridContext must be used within ScheduleGridProvider');
  }
  return context;
}

type ScheduleGridProviderProps = PropsWithChildren<{
  hourHeight: number;
  gridLinesPerHour: number;
  timeRange: { start: string; end: string };
  sizeThresholds: SizeThresholds;
}>;

export function ScheduleGridProvider({
  children,
  hourHeight,
  gridLinesPerHour,
  timeRange,
  sizeThresholds,
}: ScheduleGridProviderProps) {
  const value = useMemo<ScheduleGridContextValue>(() => {
    const startMinutes = getMinutesSinceMidnight(timeRange.start);
    const pixelsPerMinute = hourHeight / 60;

    const getItemHeight = (startTime: string, endTime: string): number => {
      const durationMinutes = getMinutesDiff(startTime, endTime);
      return durationMinutes * pixelsPerMinute;
    };

    const getItemTop = (startTime: string): number => {
      const itemMinutes = getMinutesSinceMidnight(startTime);
      return (itemMinutes - startMinutes) * pixelsPerMinute;
    };

    const getItemSize = (height: number): ScheduleItemSize => {
      if (height < sizeThresholds.minimal) return ScheduleItemSize.Minimal;
      if (height < sizeThresholds.compact) return ScheduleItemSize.Compact;
      return ScheduleItemSize.Full;
    };

    return {
      hourHeight,
      gridLinesPerHour,
      timeRange,
      getItemHeight,
      getItemTop,
      getItemSize,
    };
  }, [hourHeight, gridLinesPerHour, timeRange, sizeThresholds]);

  return <ScheduleGridContext.Provider value={value}>{children}</ScheduleGridContext.Provider>;
}
